import { LoadingOverlay } from "@src/common/LoadingOverlay";
import { APP_ROUTES, IDLE_TIMEOUT } from "@src/constants";
import { getEntryRouteDefinition } from "@src/routes/entry-route-registry";
import ClientLogsService from "@src/services/client-logs.service";
import {
  bootstrapFeatureAssignments,
  type FeatureBootstrapManifest,
  type FeatureBootstrapRole,
} from "@src/services/feature-bootstrap.service";
import { ensureUserUuid, resolveUserRoleForFlow } from "@src/utils/entry-route-bootstrap";
import { useLocation } from "@uhg-abyss/web/tools/reactRouterTools";
import type { FC, ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useApplicationContext } from "./ApplicationContext";
import { useCanvasContext } from "./CanvasContext";

export const FEATURE_DEFINITIONS = {
  COMBINED_FIND_PROVIDER: {
    key: "combined-find-provider",
    variants: ["control", "treatment"],
  },
  ENROLLMENT_CATEGORY_PACKAGE_FILTER: {
    key: "enrollment-category-package-filter",
    variants: ["control", "treatment"],
  },
  SCHEDULING_CONSOLIDATION: {
    key: "scheduling-consolidation",
    // The current experience renders all non-control arms through the
    // existing consolidated scheduling path. Keeping the arms explicit here
    // lets the API assign multiple variants without making the browser guess.
    variants: ["control", "treatment", "treatment_a", "treatment_b"],
  },
} as const;

export type FeatureName = keyof typeof FEATURE_DEFINITIONS;
export type FeatureVariant = (typeof FEATURE_DEFINITIONS)[FeatureName]["variants"][number];

interface StoredManifest {
  userUuid: string;
  leadId?: string;
  role: FeatureBootstrapRole;
  storedAt: number;
  manifest: FeatureBootstrapManifest;
}

interface FeatureManifestContextValue {
  manifest: FeatureBootstrapManifest;
  initialEntryPath: string;
  getVariant: (featureName: FeatureName) => FeatureVariant;
  recordExposure: (featureName: FeatureName, experience: string, route: string) => void;
}

const FeatureManifestContext = createContext<FeatureManifestContextValue | undefined>(undefined);
const MANIFEST_STORAGE_PREFIX = "featureManifest";
const EXPOSURE_STORAGE_PREFIX = "featureExposure";
const CANVAS_DEPENDENCY_TIMEOUT_MS = 10_000;
const CONTROL_ONLY_MANIFEST: FeatureBootstrapManifest = Object.freeze({ assignments: {} });
const PORTAL_REGISTRATION_PUBLIC_ROUTES = new Set<string>([
  APP_ROUTES.PORTAL_REGISTRATION_CALLBACK,
  APP_ROUTES.PORTAL_REGISTRATION_COMPLETE,
  APP_ROUTES.PORTAL_REGISTRATION_INVITATION_FALLBACK,
]);

const CONTACT_ROUTE_PATHS: ReadonlySet<string> = new Set([
  APP_ROUTES.CONTACT,
  APP_ROUTES.CONTACT_US,
]);

const initialPathname = (): string =>
  typeof window === "undefined" ? APP_ROUTES.GET_STARTED : window.location.pathname;

const initialEntryIsReferrer = (): boolean => initialPathname().startsWith(APP_ROUTES.REFER);

const PUBLIC_ROUTE_VARIANT: FeatureManifestContextValue["getVariant"] = () => "control";
const ignorePublicRouteExposure: FeatureManifestContextValue["recordExposure"] = () => {};

const storageKey = (userUuid: string, leadId?: string): string =>
  `${MANIFEST_STORAGE_PREFIX}:${leadId?.trim() || "browser"}:${userUuid}`;

const activityKey = (leadId?: string): string =>
  leadId?.trim() ? `lastActivityAt:${leadId.trim()}` : "lastActivityAt";

const expiryKey = (leadId?: string): string =>
  leadId?.trim() ? `sessionExpirySeconds:${leadId.trim()}` : "sessionExpirySeconds";

const readStoredManifest = (
  key: string,
  userUuid: string,
  role: FeatureBootstrapRole,
  leadId?: string,
): FeatureBootstrapManifest | null => {
  try {
    const serialized = localStorage.getItem(key);
    if (!serialized) return null;

    const stored = JSON.parse(serialized) as StoredManifest;
    const lastActivity = Number(localStorage.getItem(activityKey(leadId))) || stored.storedAt;
    const expirySeconds = Number(localStorage.getItem(expiryKey(leadId))) || IDLE_TIMEOUT;
    const isExpired = Date.now() - lastActivity >= expirySeconds * 1000;
    const leadMatches = (stored.leadId?.trim() || undefined) === (leadId?.trim() || undefined);

    if (
      stored.userUuid !== userUuid ||
      stored.role !== role ||
      !leadMatches ||
      isExpired ||
      !stored.manifest ||
      typeof stored.manifest.assignments !== "object"
    ) {
      localStorage.removeItem(key);
      return null;
    }

    return stored.manifest;
  } catch {
    return null;
  }
};

const persistManifest = (key: string, stored: StoredManifest): void => {
  try {
    localStorage.setItem(key, JSON.stringify(stored));
  } catch {
    // The in-memory manifest still keeps the current logical session stable.
  }
};

const exposureStorageKey = (
  userUuid: string,
  leadId: string | undefined,
  transactionID: string,
  featureKey: string,
  experience: string,
): string =>
  `${EXPOSURE_STORAGE_PREFIX}:${leadId?.trim() || "browser"}:${userUuid}:${transactionID}:${featureKey}:${experience}`;

const wasExposureRecorded = (key: string): boolean => {
  try {
    return localStorage.getItem(key) === "true";
  } catch {
    return false;
  }
};

const persistExposure = (key: string): void => {
  try {
    localStorage.setItem(key, "true");
  } catch {
    // The provider-local set still suppresses duplicates until the next remount.
  }
};

export const FeatureManifestProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  if (PORTAL_REGISTRATION_PUBLIC_ROUTES.has(pathname)) {
    return (
      <FeatureManifestContext.Provider
        value={{
          manifest: CONTROL_ONLY_MANIFEST,
          initialEntryPath: pathname,
          getVariant: PUBLIC_ROUTE_VARIANT,
          recordExposure: ignorePublicRouteExposure,
        }}
      >
        {children}
      </FeatureManifestContext.Provider>
    );
  }

  return <FeatureManifestProviderInternal>{children}</FeatureManifestProviderInternal>;
};

const FeatureManifestProviderInternal: FC<{ children: ReactNode }> = ({ children }) => {
  const { data, setData, setDataPatch, clearSession } = useApplicationContext();
  const { isInSalesforce, isLoading: canvasIsLoading } = useCanvasContext();
  const [manifest, setManifest] = useState<FeatureBootstrapManifest | null>(null);
  const [dependencyTimedOut, setDependencyTimedOut] = useState(false);
  const entryPathRef = useRef(initialPathname());
  const entryRoute = getEntryRouteDefinition(entryPathRef.current);
  // Contact pages wipe session data on mount; skipping identity setup there prevents
  // userUuid regeneration and manifest re-bootstrap from remounting them in a loop.
  const isContactRoute = CONTACT_ROUTE_PATHS.has(initialPathname());
  const entryIdentityReady =
    !entryRoute || isContactRoute || data.entryRouteIdentityPath === entryRoute.path;
  const initialReferrerFlow = useRef(
    entryRoute
      ? entryRoute.path === APP_ROUTES.REFER
      : (data.isReferrerFlow ?? initialEntryIsReferrer()),
  );
  const priorStorageKeyRef = useRef<string | undefined>(undefined);
  const manifestStorageKeyRef = useRef<string | undefined>(undefined);
  const mismatchLogsRef = useRef(new Set<string>());
  const exposureLogsRef = useRef(new Set<string>());
  const dependencyFailureLoggedRef = useRef(false);

  useEffect(() => {
    const timeout = setTimeout(() => setDependencyTimedOut(true), CANVAS_DEPENDENCY_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (!entryRoute || entryIdentityReady) return;

    if (entryRoute.identityPolicy === "reset-session") {
      clearSession();
    }

    const shouldRegenerate =
      entryRoute.identityPolicy === "reset-session" ||
      entryRoute.identityPolicy === "regenerate-uuid" ||
      Boolean(data.isReturningFromContactFlow);
    const identity = ensureUserUuid({
      currentUserUuid: data.userUuid,
      setData,
      forceRegenerate: shouldRegenerate,
    });

    setDataPatch({
      userUuid: identity.userUuid,
      isReferrerFlow: entryRoute.path === APP_ROUTES.REFER,
      userRole: entryRoute.path === APP_ROUTES.REFER ? "referrer" : "patient",
      isReturningFromContactFlow: false,
      entryRouteIdentityPath: entryRoute.path,
    });
  }, [
    clearSession,
    data.isReturningFromContactFlow,
    data.userUuid,
    entryIdentityReady,
    entryRoute,
    setData,
    setDataPatch,
  ]);

  const role = useMemo<FeatureBootstrapRole | undefined>(() => {
    if (canvasIsLoading) return undefined;
    return resolveUserRoleForFlow(isInSalesforce, initialReferrerFlow.current);
  }, [canvasIsLoading, isInSalesforce]);

  useEffect(() => {
    if (!role) return;
    if (data.userRole !== role) setData("userRole", role);
    if (initialReferrerFlow.current && !data.isReferrerFlow) setData("isReferrerFlow", true);
  }, [data.isReferrerFlow, data.userRole, role, setData]);

  const waitingForCanvasLead =
    isInSalesforce && !dependencyTimedOut && (!data.salesforceLeadId || !data.canvasPrefillVersion);
  const canvasDependencyFailed =
    isInSalesforce && dependencyTimedOut && (!data.salesforceLeadId || !data.canvasPrefillVersion);

  useEffect(() => {
    if (!entryIdentityReady || !role || canvasIsLoading || waitingForCanvasLead || data.userUuid)
      return;
    ensureUserUuid({ setData });
  }, [canvasIsLoading, data.userUuid, entryIdentityReady, role, setData, waitingForCanvasLead]);

  const userUuid = data.userUuid;
  const leadId = data.salesforceLeadId?.trim();
  const currentStorageKey = userUuid ? storageKey(userUuid, leadId) : undefined;
  const activeManifest = manifestStorageKeyRef.current === currentStorageKey ? manifest : null;

  useEffect(() => {
    if (priorStorageKeyRef.current && priorStorageKeyRef.current !== currentStorageKey) {
      localStorage.removeItem(priorStorageKeyRef.current);
      manifestStorageKeyRef.current = undefined;
      setManifest(null);
      mismatchLogsRef.current.clear();
      exposureLogsRef.current.clear();
    }
    priorStorageKeyRef.current = currentStorageKey;
  }, [currentStorageKey]);

  useEffect(() => {
    if (!entryIdentityReady || !userUuid || !role || !currentStorageKey || waitingForCanvasLead)
      return;
    let cancelled = false;

    if (canvasDependencyFailed) {
      if (!dependencyFailureLoggedRef.current) {
        dependencyFailureLoggedRef.current = true;
        ClientLogsService.logError(
          userUuid,
          "FeatureManifestContext.bootstrap",
          "Feature bootstrap dependency timed out; using empty assignment manifest",
        );
      }
      manifestStorageKeyRef.current = currentStorageKey;
      setManifest(CONTROL_ONLY_MANIFEST);
      return;
    }

    const restored = readStoredManifest(currentStorageKey, userUuid, role, leadId);
    if (restored) {
      if (!data.userRecordCreated) setData("userRecordCreated", true);
      manifestStorageKeyRef.current = currentStorageKey;
      setManifest(restored);
      return;
    }

    setManifest(null);
    bootstrapFeatureAssignments({ user_uuid: userUuid, role }).then((resolvedManifest) => {
      if (cancelled) return;
      if (resolvedManifest.transactionID && !data.userRecordCreated) {
        setData("userRecordCreated", true);
      }
      persistManifest(currentStorageKey, {
        userUuid,
        leadId,
        role,
        storedAt: Date.now(),
        manifest: resolvedManifest,
      });
      manifestStorageKeyRef.current = currentStorageKey;
      setManifest(resolvedManifest);
    });

    return () => {
      cancelled = true;
    };
  }, [
    canvasDependencyFailed,
    currentStorageKey,
    data.userRecordCreated,
    entryIdentityReady,
    leadId,
    role,
    setData,
    userUuid,
    waitingForCanvasLead,
  ]);

  const getVariant = useCallback(
    (featureName: FeatureName): FeatureVariant => {
      const definition = FEATURE_DEFINITIONS[featureName];
      const assigned = activeManifest?.assignments[definition.key];
      if (definition.variants.some((variant) => variant === assigned)) {
        return assigned as FeatureVariant;
      }

      const mismatchKey = `${definition.key}:${assigned ?? "missing"}`;
      if (activeManifest && !mismatchLogsRef.current.has(mismatchKey)) {
        mismatchLogsRef.current.add(mismatchKey);
        ClientLogsService.logError(
          userUuid || "unknown",
          "FeatureManifestContext.getVariant",
          `Feature manifest contract mismatch for ${definition.key}`,
        );
      }
      return "control";
    },
    [activeManifest, userUuid],
  );

  const recordExposure = useCallback(
    (featureName: FeatureName, experience: string, route: string) => {
      if (!activeManifest?.transactionID || !userUuid) return;
      const definition = FEATURE_DEFINITIONS[featureName];
      const variant = getVariant(featureName);
      const exposureKey = exposureStorageKey(
        userUuid,
        leadId,
        activeManifest.transactionID,
        definition.key,
        experience,
      );
      if (exposureLogsRef.current.has(exposureKey) || wasExposureRecorded(exposureKey)) return;
      exposureLogsRef.current.add(exposureKey);
      persistExposure(exposureKey);
      ClientLogsService.logFeatureExposure(userUuid, {
        bootstrapTransactionID: activeManifest.transactionID,
        featureKey: definition.key,
        variant,
        experience,
        route,
        eventTimestamp: new Date().toISOString(),
      });
    },
    [activeManifest?.transactionID, getVariant, leadId, userUuid],
  );

  const value = useMemo(
    () => ({
      manifest: activeManifest ?? { assignments: {} },
      initialEntryPath: entryPathRef.current,
      getVariant,
      recordExposure,
    }),
    [activeManifest, getVariant, recordExposure],
  );

  if (!activeManifest) {
    return <LoadingOverlay isLoading={true} ariaLoadingLabel="Loading feature assignments..." />;
  }

  return (
    <FeatureManifestContext.Provider value={value}>{children}</FeatureManifestContext.Provider>
  );
};

export const useFeatureManifest = (): FeatureManifestContextValue => {
  const context = useContext(FeatureManifestContext);
  if (!context) throw new Error("useFeatureManifest must be used within FeatureManifestProvider");
  return context;
};

export const useFeatureExposure = (
  featureName: FeatureName,
  experience: string,
  route: string,
  didRender: boolean,
): void => {
  const context = useContext(FeatureManifestContext);
  useEffect(() => {
    if (didRender) context?.recordExposure(featureName, experience, route);
  }, [context, didRender, experience, featureName, route]);
};
